/*
 * 비밀번호 변경 팝업
 */
import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Alert,
  InputAdornment,
  IconButton,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useApiUtil } from "@/utils/apiUtil";
import CommonPopup from "@/comp/CommonPopup";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useCommon } from "@/utils/common";

const PasswordPopup = ({ open, onClose, onSubmit }) => {
  const commonJs = useCommon();
  /* ================= 상태 ================= */
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const { gfnCallApi } = useApiUtil();

  /* ================= 공통 props ================= */
  const commonProps = {
    fullWidth: true,
    size: "small",
    variant: "outlined",
  };

  /* ================= 비밀번호 규칙 ================= */
  const passwordRules = {
    length: newPassword.length >= 8,
    eng: /[a-zA-Z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
  };

  const isPasswordValid =
    passwordRules.length &&
    passwordRules.eng &&
    passwordRules.number &&
    passwordRules.special;

  /* ================= 저장 ================= */
  const handleSave = async () => {
    if (!oldPassword || !newPassword || !newPasswordConfirm) {
      setErrorMsg("모든 항목을 입력해주세요.");
      return;
    }

    if (oldPassword === newPassword) {
      setErrorMsg("신규 비밀번호와 변경 비밀번호가 동일합니다.");
      return;
    }    

    if (!isPasswordValid) {
      setErrorMsg("신규 비밀번호가 규칙을 만족하지 않습니다.");
      return;
    }

    if (newPassword !== newPasswordConfirm) {
      setErrorMsg("신규 비밀번호가 일치하지 않습니다.");
      return;
    }

    setErrorMsg("");

    const result = await gfnCallApi({
      method: "post",
      url: "changePswd",
      data: { oldPassword: oldPassword, newPassword: newPassword },
      showMsg: false,
    });

    if (!result) return;
    if (result.code < 0) {
        commonJs.showToastMsg(
          result.message
        );
        return;
    }

  if (result.result === 1) {
    console.log("비밀번호 변경 성공:", result);

    // 비밀번호 변경 후 기존 인증을 종료하고 로그인 화면으로 이동
    commonJs.showToastMsg("비밀번호가 변경되었습니다. 다시 로그인해 주세요.");
    handleClose();
    await onSubmit?.();

    return;
    
  } else {
    commonJs.showToastMsg(
      result.message || "오류가 발생하였습니다."
    );
  }

    //handleClose();
  };

  /* ================= 닫기 ================= */
  const handleClose = () => {
    setOldPassword("");
    setNewPassword("");
    setNewPasswordConfirm("");
    setErrorMsg("");
    setShowOld(false);
    setShowNew(false);
    setShowConfirm(false);
    onClose();
  };

  /* ================= footer ================= */
  const footerActions = [
    { label: "변경", onClick: handleSave },
  ];

  const RuleItem = ({ label, ok }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
      {ok ? (
        <CheckCircleIcon fontSize="inherit" sx={{ color: "success.main" }} />
      ) : (
        <CancelIcon fontSize="inherit" sx={{ color: "text.disabled" }} />
      )}
      <Typography
        variant="caption"
        sx={{ color: ok ? "success.main" : "text.secondary" }}
      >
        {label}
      </Typography>
    </Box>
  );  

  useEffect(() => {
    if (open) {
      setOldPassword("");
      setNewPassword("");
      setNewPasswordConfirm("");
    }
  }, [open]);  
  /* ================= 렌더 ================= */
  return (
    <CommonPopup
      open={open}
      onClose={handleClose}
      title="비밀번호 변경"
      width={420}
      height={320}
      footerActions={footerActions}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        {errorMsg && <Alert severity="error">{errorMsg}</Alert>}

        {/* 기존 비밀번호 */}
        <TextField
          {...commonProps}
          label="기존 비밀번호"
          type={showOld ? "text" : "password"}
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          autoComplete="new-password"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowOld(!showOld)}>
                  {showOld ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* 신규 비밀번호 */}
        <TextField
          {...commonProps}
          label="신규 비밀번호"
          type={showNew ? "text" : "password"}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          error={newPassword.length > 0 && !isPasswordValid}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowNew(!showNew)}>
                  {showNew ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* 비밀번호 규칙 안내 */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            ml: 0.5,
            mb: 1,
          }}
        >
          <RuleItem label="8자 이상" ok={passwordRules.length} />
          <RuleItem label="영문" ok={passwordRules.eng} />
          <RuleItem label="숫자" ok={passwordRules.number} />
          <RuleItem label="특수문자" ok={passwordRules.special} />
        </Box>

        {/* 신규 비밀번호 확인 */}
        <TextField
          {...commonProps}
          label="신규 비밀번호 확인"
          type={showConfirm ? "text" : "password"}
          value={newPasswordConfirm}
          onChange={(e) => setNewPasswordConfirm(e.target.value)}
          autoComplete="new-password"
          error={
            newPasswordConfirm.length > 0 &&
            newPassword !== newPasswordConfirm
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirm(!showConfirm)}>
                  {showConfirm ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityIcon />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </CommonPopup>
  );
};

export default PasswordPopup;
